import { toast } from "react-toastify";
import { Button } from "react-bootstrap";
import { useAuth } from "../../../../firebase/AuthProvider";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState, useCallback } from "react";
import { SignatureField, SignatureStatus, SignatureType } from "../../../../interfaces/file/fileSignature/SignatureBox";
import { getDriveFileById, getFileById } from "../../../../services/fileServices/FileService";
import DriveFile from "../../../../interfaces/file/DriveFile";
import { debounce } from "lodash";
import { cancelSignatureRequestById, createSignatureRequest, searchEmails, submitSignature, submitSignatureCancelReq } from "../../../../services/fileServices/signatures/SignatureService";
import { getTaskById } from "../../../../services/taskServices/TaskService";
import { CreateSignatureRequestPayload } from "../../../../interfaces/file/fileSignature/SignatureCreationRequests";
import PDFViewer from "./PDFViewer";
import PDFEditorHeader from "./editorComponents/PDFEditorHeader";
import PDFViewerControls from "./editorComponents/PDFViewerControls";
import PDFEditorSidebar from "./editorComponents/PDFEditorSidebar";
import { FileNavigationState } from "../../../../interfaces/task/Task";
import ErrorDocProgressLoader from "./guestSignComponents/ErrorDocProgressLoader";
import { PeoplePickerUser } from "./editorComponents/SignatureBoxesPanel";
import { showConfirmationDialog } from "../../../modal/ConfimationModal/Confimation";

const PDFEditor: React.FC = () => {
  const { currentUser } = useAuth();
  const { fileId } = useParams();
  const navigate = useNavigate();

  const location = useLocation();
  const state = location.state as FileNavigationState | null;

  const [fileBlobUrl, setFileBlobUrl] = useState<string | null>(null);
  const [fileMetadata, setFileMetadata] = useState<DriveFile | null>(null);
  const [signatureFields, setSignatureFields] = useState<SignatureField[]>([]);
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);
  const [activeNav, setActiveNav] = useState<string | null>("document");
  const [suggestions, setSuggestions] = useState<PeoplePickerUser[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isPlacingBox, setIsPlacingBox] = useState<boolean>(false);
  const [userCanEdit, setUserCanEdit] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false);
  const [authorId, setAuthorId] = useState<string | undefined>(undefined);
  const [taskStatus, setTaskStatus] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Progress UI for document loading (increments by 5% every 100ms while fetching)
  const [loadProgress, setLoadProgress] = useState<number>(0);
  const progressIntervalRef = useRef<number | null>(null);

  // Ref for logic (stable), State for UI (reactive)
  const initialFieldIdsRef = useRef<Set<string>>(new Set());
  const [initialFieldIds, setInitialFieldIds] = useState<Set<string>>(new Set());

  const [signatureStatus, setSignatureStatus] = useState<SignatureStatus>();

//   useEffect(() => {
//     if (signatureStatus === "pending" && fileId) {

//       navigate(`/view/file/${fileId}`, {
//         replace: true,
//         state: {
//           taskId: state?.taskId ?? null,
//           taskTitle: state?.taskTitle ?? null,
//         } satisfies FileNavigationState,
//       });
//     }
//   }, [signatureStatus, fileId, navigate, state?.taskId, state?.taskTitle]);

  useEffect(() => {
    if (selectedBoxId && scrollContainerRef.current) {
      const selectedField = signatureFields.find(
        (field) => field.id === selectedBoxId
      );
      if (selectedField) {
        const yPos = selectedField.y;
        scrollContainerRef.current.scrollTo({
          top: yPos - 50,
          behavior: "smooth",
        });
      }
    }
  }, [selectedBoxId, signatureFields]);

  useEffect(() => {
    const checkUserPermissions = async () => {
      if (!currentUser || !fileId) {
        setUserCanEdit(false);
        return;
      }

      try {
        const authToken = await currentUser.getIdToken();

        // Get file metadata to check author
        const file = await getDriveFileById(fileId, authToken);
        const isFileAuthor = file?.authorId === currentUser.uid;

        let isTaskWithSC = false;

        // Check task status if taskId is available
        if (state?.taskId) {
          try {
            const task = await getTaskById(state.taskId.toString(), authToken);
            // Task is with SC if status is not pending review
            // Common statuses that indicate task is still with SC:
            // "Not Started", "In Progress", "On Hold", etc.
            // Statuses that indicate task is NOT with SC:
            // "Pending Review", "Pending Supervisor Review", "Pending PM Review", etc.
            const pendingReviewStatuses = [
              "pending review",
              "pending supervisor review",
              "pending pm review",
              "pending review supervisor",
              "pending review pm"
            ];

            const taskStatusName = task?.status?.name?.toLowerCase() || "";
            isTaskWithSC = !pendingReviewStatuses.some(status =>
              taskStatusName.includes(status)
            );

            setTaskStatus(task?.status?.name || null);
          } catch (error) {
            console.error("Error fetching task status:", error);
            // If we can't fetch task status, default to file author check only
          }
        }

        // User can edit if they are the file author OR if task is still with SC
        const canEdit = isFileAuthor || isTaskWithSC;
        setUserCanEdit(canEdit);

      } catch (error) {
        console.error("Error checking permissions:", error);
        setUserCanEdit(false);
      }
    };

    checkUserPermissions();
  }, [currentUser, fileId, navigate, state?.taskId]);

  // ✅ FIXED: Separated "API Load" Logic (Smart Merge)
  const handleApiDataLoaded = useCallback((backendFields: SignatureField[]) => {
    const newBackendIds = new Set(backendFields.map(f => f.id));
    const oldBackendIds = initialFieldIdsRef.current;

    console.group("Handle API Data Load");
    console.log("Incoming API Fields:", backendFields.length);
    console.log("Old DB IDs Known:", oldBackendIds.size);

    if (backendFields.length === 0 && oldBackendIds.size > 0) {
      console.warn("⚠️ ALERT: API returned EMPTY list, but we previously had DB boxes. Logic will assume they were deleted.");
    }

    setSignatureFields(prevFields => {
      // Logic: Find local drafts (items NOT in the new backend list AND NOT in the old backend list)
      const localDrafts = prevFields.filter(pf => {
        const isNew = !newBackendIds.has(pf.id);
        const wasUnknown = !oldBackendIds.has(pf.id);

        // If it was in Old IDs but NOT in New IDs, it means it was DELETED.
        // We only keep it if it was NEVER known to the DB (True Local Draft).
        return isNew && wasUnknown;
      });

      console.log("Preserved Local Drafts:", localDrafts.length);
      const merged = [...backendFields, ...localDrafts];
      console.log("Final Merged State:", merged.length);
      console.groupEnd();

      return merged;
    });

    initialFieldIdsRef.current = newBackendIds;
    setInitialFieldIds(newBackendIds);
    setInitialLoadComplete(true);

    if (backendFields.length === 0) {
      setSignatureStatus(undefined);
      return;
    }

    const hasPending = backendFields.some(
      (box) => !box.signatureValue || !box.signedAt
    );
    setSignatureStatus(hasPending ? "pending" : "success");
  }, []); // Stable dependency

  // ✅ FIXED: Separated "Local Update" Logic (Direct Set)
  const handleLocalStateUpdate = useCallback((updatedFields: SignatureField[]) => {
    console.log("Local Update Triggered. New Count:", updatedFields.length);
    setSignatureFields(updatedFields);
  }, []);

  useEffect(() => {
    let objectUrl: string | null = null;

    const fetchFile = async () => {
      if (!fileId || !currentUser) return;
      setFileBlobUrl(null);
      setFileMetadata(null);
      // Start the incremental progress simulation
      setLoadProgress(0);
      if (progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
      }
      progressIntervalRef.current = window.setInterval(() => {
        setLoadProgress(prev => Math.min(prev + 5, 95));
      }, 300);

      try {
        const authToken = await currentUser.getIdToken();
        const file = await getDriveFileById(fileId, authToken);
        if (file) {
          if (file.authorId) setAuthorId(file.authorId);
          setFileMetadata(file);
          const url = await getFileById(fileId, authToken);
          if (url) {
            const response = await fetch(url, {
              headers: { Authorization: `Bearer ${authToken}` },
            });
            const blob = await response.blob();
            objectUrl = URL.createObjectURL(blob);
            // Complete progress and show 100% briefly before rendering viewer
            setLoadProgress(100);
            if (progressIntervalRef.current) {
              window.clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
            setTimeout(() => {
              setFileBlobUrl(objectUrl);
            }, 300);
          }
        }
      } catch (error) {
        console.error("Error fetching file:", error);
        toast.error("Failed to load the document.");
        // Ensure progress reaches 100% and stop interval on error
        if (progressIntervalRef.current) {
          window.clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        setLoadProgress(100);
      }
    };

    fetchFile();
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [fileId, currentUser]);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      if (entries[0]) setContainerWidth(entries[0].contentRect.width);
    });
    const container = pdfContainerRef.current;
    if (container) observer.observe(container);
    return () => {
      if (container) observer.unobserve(container);
    };
  }, [fileBlobUrl]);

  const handleBoxSelect = (boxId: string) => {
    setActiveNav("signature-boxes");
    setSelectedBoxId(boxId);
  };

  // const debouncedSearch = debounce(async (query: string) => {
  //   if (!currentUser) return;
  //   const authToken = await currentUser.getIdToken();
  //   if (!query) return setSuggestions([]);
  //   const results = await searchEmails(query, authToken);
  //   setSuggestions(results || []);
  //   setShowDropdown(true);
  // }, 300);

  const debouncedSearch = useRef<any>(null);

  useEffect(() => {
    debouncedSearch.current = debounce(async (query: string) => {
      if (!currentUser || !query.trim()) {
        setSuggestions([]);
        setShowDropdown(false);
        setIsLoading(false);
        return;
      }

      if (query === selectedBox?.signerEmail) {
        setIsLoading(false);
        return;
      }
      const authToken = await currentUser.getIdToken();
      setIsLoading(true);

      const results = await searchEmails(query, authToken);

      setSuggestions(results || []);
      setShowDropdown(true);
      setIsLoading(false);

    }, 300);
  }, [currentUser]);

  // const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const value = e.target.value;
  //   updateSelectedBox({ signerEmail: value });
  //   debouncedSearch(value);
  // };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    updateSelectedBox({ signerEmail: value });
    debouncedSearch.current(value);
  };

  // const handleSelect = (email: string) => {
  //   updateSelectedBox({ signerEmail: email });
  //   setShowDropdown(false);
  //   setSuggestions([]);
  // };

  const handleSelect = (email: string) => {
    debouncedSearch.current?.cancel(); // 🔥 ADD THIS LINE

    updateSelectedBox({ signerEmail: email });
    setShowDropdown(false);
    setSuggestions([]);
    setIsLoading(false);
  };


  // useEffect(() => {
  //   return () => {
  //     debouncedSearch.cancel();
  //   };
  // }, []);

  const selectedBox = signatureFields.find((b) => b.id === selectedBoxId);

  const updateSelectedBox = (updates: Partial<SignatureField>) => {
    if (!selectedBox) return;
    setSignatureFields((prev) =>
      prev.map((box) =>
        box.id === selectedBoxId ? { ...box, ...updates } : box
      )
    );
  };

  const handlePlaceBox = (pageNumber: number, x: number, y: number) => {
    // 1. Inherit email from previous box (Sticky Mode)
    const previousBox = signatureFields.find(b => b.id === selectedBoxId);
    const inheritedEmail = previousBox?.signerEmail || "";

    const newBox: SignatureField = {
      id: crypto.randomUUID(),
      pageNumber,
      x,
      y,
      width: 150,
      height: 40,
      signerEmail: inheritedEmail,
      signatureValue: "",
      signatureType: SignatureType.SIGNATURE,
    };

    setSignatureFields((prev) => [...prev, newBox]);
    setSelectedBoxId(newBox.id);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isPlacingBox) {
        setIsPlacingBox(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPlacingBox]);

  const handleSignatureSubmit = async () => {
    if (!fileId || !currentUser) return;
    const ownerId = currentUser.uid;
    const authToken = await currentUser.getIdToken();
    setIsRequestLoading(true);
    const response = await submitSignature(authToken, fileId, ownerId);
    if (response) {
      toast.success("Signature File saved successfully!");
    } else {
      toast.error("All Signers have not signed!");
    }
    setIsRequestLoading(false);
  };

  const handleSignatureCancelation = async () => {
    if (!fileId || !currentUser) return;
    const ownerId = currentUser.uid;
    const authToken = await currentUser.getIdToken();
    setIsRequestLoading(true);
    try {
      // const result = await submitSignatureCancelReq(
      //   authToken,
      //   fileId,
      //   ownerId,
      //   "canceled"
      // );
      const result = await cancelSignatureRequestById(
        authToken, 
        signatureFields[0].id // here if we cancel any field from this function, we will cancel the entire request. 
      )
      if (result.success) {
        toast.success(result.message);
        navigate("/tasks");
      } else {
        toast.error(result.message);
      }
      setShowConfirmModal(false);
      setSignatureFields([]);
    } catch (error: any) {
      console.log("Failed to canceles request", error);
      toast.error(error.message || "Faild to send the Request");
    } finally {
      setIsRequestLoading(false);
    }
  };

  const handleRequestSignature = async () => {
    if (!fileId || !currentUser) return;
    if (signatureFields.length === 0) {
      setErrorMessage("Please add at least one signature box before requesting.");
      return;
    }
    setIsRequestLoading(true);
    try {
      const authToken = await currentUser.getIdToken();
      const mainSignerEmail = signatureFields[0]?.signerEmail || currentUser.email || "";

      const payload: CreateSignatureRequestPayload = {
          fileId,
          signatureBoxes: signatureFields.map((field) => ({
              ...(initialFieldIds.has(field.id) && { id: field.id }),
              pageNumber: field.pageNumber,
              x: field.x,
              y: field.y,
              width: field.width,
              height: field.height,
              signatureValue: field.signatureValue ?? "",
              assignedSignerEmail: field.signerEmail ?? mainSignerEmail,
              boxType: field.signatureType,
          })),
          taskId: state?.taskId || null
      };

      const result = await createSignatureRequest(payload, authToken);
      if (result) {
        toast.success("Signature request created successfully!");
        navigate(`/view/file/${fileId}`);
      } else {
        setErrorMessage("Error creating signature request.");
      }
    } catch (error: any) {
      console.error("Error creating signature request:", error);
      setErrorMessage(error?.message || "Error creating signature request.");
    } finally {
      setIsRequestLoading(false);
    }
  };

  const toggleNav = (elementName: string) => {
    setActiveNav(activeNav === elementName ? null : elementName);
  };

  const toggleSelectedBox = (boxId: string) => {
    if (selectedBoxId === boxId) {
      setSelectedBoxId(null);
    } else {
      const field = signatureFields.find((f) => f.id === boxId);
      if (field) {
        setCurrentPage(field.pageNumber);
        setSelectedBoxId(boxId);
      }
    }
  };

  const groupedSignatureFields = signatureFields.reduce((acc, field) => {
    const page = field.pageNumber;
    if (!acc[page]) acc[page] = [];
    acc[page].push(field);
    return acc;
  }, {} as { [key: number]: SignatureField[] });

  useEffect(() => {
    if (initialLoadComplete) {
      const hasChanges = signatureFields.some(field => !initialFieldIds.has(field.id));
      setHasUnsavedChanges(hasChanges);
    }
  }, [signatureFields, initialFieldIds, initialLoadComplete]);

  const handleBack = async () => {
    if (hasUnsavedChanges) {
      const confirmed = await showConfirmationDialog(
        "Unsaved Changes",
        "You have unsaved changes. Are you sure you want to go back?",
        "Yes, go back",
        "Cancel"
      );
      if (confirmed) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const hasSignatureBoxes = signatureFields.length > 0;

  return (
    <>
      <div className="">
        <div className="common_card">
          <PDFEditorHeader
            fileMetadata={fileMetadata}
            isRequestLoading={isRequestLoading}
            handleRequestSignature={handleRequestSignature}
            currentUser={currentUser}
            authorId={authorId}
            setShowConfirmModal={setShowConfirmModal}
            handleSignatureSubmit={handleSignatureSubmit}
            userCanEdit={userCanEdit}
            signatureStatus={signatureStatus}
            handleBack={handleBack}
            hasSignatureBoxes={hasSignatureBoxes}
          />

          {fileBlobUrl && userCanEdit ? (
            <div className="sign_body">
              <div className="sign_left">
                <div className="sign_right_body" ref={pdfContainerRef}>
                  <PDFViewerControls
                    numPages={numPages}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    isPlacingBox={isPlacingBox}
                    setIsPlacingBox={setIsPlacingBox}
                  />
                  <div
                    ref={scrollContainerRef}
                    className="pdfViewer"
                    style={{ maxHeight: "calc(100vh - 250px)" }}
                  >
                    <PDFViewer
                      fileUrl={fileBlobUrl}
                      containerWidth={containerWidth}
                      canEdit={true}
                      selectedSignerEmail=""
                      // ✅ CRITICAL: Using separated handlers
                      onSignatureFieldsChange={handleLocalStateUpdate}
                      onApiDataLoaded={handleApiDataLoaded}
                      signatureFields={signatureFields}
                      onBoxSelect={handleBoxSelect}
                      isPlacingBox={isPlacingBox}
                      onPlaceBox={handlePlaceBox}
                      currentPage={currentPage}
                      setNumPages={setNumPages}
                      selectedBoxId={selectedBoxId}
                      initialFieldIds={initialFieldIds}
                    />
                  </div>
                </div>
              </div>
              <PDFEditorSidebar
                activeNav={activeNav}
                toggleNav={toggleNav}
                fileMetadata={fileMetadata}
                groupedSignatureFields={groupedSignatureFields}
                signatureFields={signatureFields}
                selectedBoxId={selectedBoxId}
                toggleSelectedBox={toggleSelectedBox}
                selectedBox={selectedBox}
                updateSelectedBox={updateSelectedBox}
                handleInputChange={handleInputChange}
                showDropdown={showDropdown}
                setShowDropdown={setShowDropdown}
                suggestions={suggestions}
                handleSelect={handleSelect}
                isLoading={isLoading}
                signedStatus={signatureStatus}
                taskId={state?.taskId || null}
                taskTitle={state?.taskTitle}
              />
            </div>
          ) : (
            !isRequestLoading && <ErrorDocProgressLoader progress={loadProgress} />
          )}
        </div>
      </div>
      {showConfirmModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg max-w-sm w-full">
            <h2 className="text-lg font-semibold mb-4">Confirmation</h2>
            <p className="mb-6">
              Are you sure you want to cancel the signature request?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                className="btn_sm"
                variant="secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="btn_sm"
                variant="primary"
                onClick={handleSignatureCancelation}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PDFEditor;